import backend from 'utils/backend'

export const SET_GRANTFIT_ERROR = 'SET_GRANTFIT_ERROR'
export const SET_GRANTFIT_QUOTA = 'SET_GRANTFIT_QUOTA'
export const SET_GRANTFIT_SAVED_DETAILS = 'SET_GRANTFIT_SAVED_DETAILS'
export const SET_GRANTFIT_SUMMARIES_LOADING = 'SET_GRANTFIT_SUMMARIES_LOADING'
export const SET_GRANTFIT_SUMMARIES = 'SET_GRANTFIT_SUMMARIES'
export const UPDATE_GRANTFIT_SUMMARY = 'UPDATE_GRANTFIT_SUMMARY'

const buildGrantFitErrorPayload = (code, err) => {
  console.error('GrantFit error:', code, err)
  const response = err && err.response ? err.response.data : null
  return { error: { code, response } }
}

export const clearGrantFitError = async (dispatch, getState) => {
  const {
    grantfit: { error }
  } = getState()
  if (error) dispatch({ type: SET_GRANTFIT_ERROR, payload: { error: null } })
}

export const setGrantFitSummariesLoading = newLoadingState => async dispatch => {
  dispatch({ type: SET_GRANTFIT_SUMMARIES_LOADING, payload: { isLoading: newLoadingState } })
}

export const getGrantFitSummariesForFunder =
  (funderId, setLoadingState = true) =>
  async dispatch => {
    dispatch(clearGrantFitError)

    if (setLoadingState) dispatch(setGrantFitSummariesLoading(true))

    try {
      const { data } = await backend.get(`api/grantfit/?funder=${funderId}`)
      if (data.results) {
        dispatch({ type: SET_GRANTFIT_SUMMARIES, payload: { summaries: data.results } })
      }
      if (data.quota) {
        dispatch({ type: SET_GRANTFIT_QUOTA, payload: { quota: data.quota } })
      }
    } catch (err) {
      dispatch({
        type: SET_GRANTFIT_ERROR,
        payload: buildGrantFitErrorPayload('getSummariesFailed', err)
      })
    }
    dispatch(setGrantFitSummariesLoading(false))
  }

export const getSavedGrantFitApplicantDetails = () => async dispatch => {
  dispatch(clearGrantFitError)

  try {
    const { data } = await backend.get('api/grantfit/details/?primary=true')
    if (data.count > 0) {
      dispatch({ type: SET_GRANTFIT_SAVED_DETAILS, payload: { details: data.results[0] } })
    }
  } catch (err) {
    dispatch({
      type: SET_GRANTFIT_ERROR,
      payload: buildGrantFitErrorPayload('getApplicantDetailsFailed', err)
    })
  }
}

export const saveGrantFitApplicantDetails =
  ({ applicantText, files, documents, primary }) =>
  async dispatch => {
    dispatch(clearGrantFitError)

    const formdata = new FormData()
    if (applicantText) formdata.append('applicantText', applicantText)
    if (files) {
      for (const file of files) {
        formdata.append('files', file.file)
      }
    }
    if (documents) {
      for (const document of documents) {
        formdata.append('document_ids', document.id)
      }
    }
    if (primary) {
      formdata.append('primary', 'true')
    }

    try {
      const { data } = await backend.post('api/grantfit/details/', formdata, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      if (primary) {
        dispatch({ type: SET_GRANTFIT_SAVED_DETAILS, payload: { details: data } })
      }
      return { success: true, id: data.id }
    } catch (err) {
      dispatch({
        type: SET_GRANTFIT_ERROR,
        payload: buildGrantFitErrorPayload('saveApplicantDetailsFailed', err)
      })
      return { success: false, error: err, throttled: err.response?.status === 429 }
    }
  }

export const markGrantFitApplicantDetailsAsPrimary = detailsId => async dispatch => {
  dispatch(clearGrantFitError)

  try {
    const { data } = await backend.patch(`api/grantfit/details/${detailsId}/`, { primary: true })
    dispatch({ type: SET_GRANTFIT_SAVED_DETAILS, payload: { details: data } })
    return { success: true, id: data.id }
  } catch (err) {
    dispatch({
      type: SET_GRANTFIT_ERROR,
      payload: buildGrantFitErrorPayload('markDetailsAsPrimaryFailed', err)
    })
    return { success: false, error: err, throttled: err.response?.status === 429 }
  }
}

const TIMEOUT_THRESHOLD = 3 * 60 * 1000 // 3 minutes
const PROCESSING_STATUSES = ['pending', 'processing']
const COMPLETED_STATUSES = ['completed', 'error']

export const updateGrantFitSummaryStatus = summaryId => async (dispatch, getState) => {
  dispatch(clearGrantFitError)

  const {
    grantfit: { summaries }
  } = getState()
  const summary = summaries.find(s => s.id === summaryId)

  try {
    const { data } = await backend.get(`api/grantfit/${summaryId}/`)

    const elapsed = new Date() - summary.requestSentAt
    const timedOut = elapsed >= TIMEOUT_THRESHOLD && !COMPLETED_STATUSES.includes(data.status)

    const isProcessing = !timedOut && PROCESSING_STATUSES.includes(data.status)

    if (isProcessing) {
      setTimeout(() => dispatch(updateGrantFitSummaryStatus(summaryId)), 5000)
    } else {
      dispatch(getGrantFitSummariesForFunder(summary.funder, false))
    }
  } catch (err) {
    dispatch({
      type: SET_GRANTFIT_ERROR,
      payload: buildGrantFitErrorPayload('updateStatusFailed', err)
    })
  }
}

export const runGrantFitAnalysis =
  ({ funderId, programId, applicantDetailsId }) =>
  async dispatch => {
    dispatch(clearGrantFitError)

    try {
      const { data } = await backend.post('api/grantfit/', {
        funder: funderId,
        program: programId,
        applicantDetailsId
      })
      dispatch({
        type: UPDATE_GRANTFIT_SUMMARY,
        payload: {
          summary: { ...data, isProcessing: true, requestSentAt: new Date() }
        }
      })
      setTimeout(() => dispatch(updateGrantFitSummaryStatus(data.id)), 1000)
      return { success: true, id: data.id }
    } catch (err) {
      dispatch({
        type: SET_GRANTFIT_ERROR,
        payload: buildGrantFitErrorPayload('runAnalysisFailed', err)
      })
      return { success: false, error: err }
    }
  }
